import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, X, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  white_collar: "Admin",
  blue_collar: "Blue Collar",
  driver: "Driver",
  manual_entry: "Manual",
};

const monthLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

// Actual calendar days in the month for an ISO date like "2026-07-01".
// Used as the salary divisor so Monthly Summary matches the Payroll engine
// (which pro-rates gross by real days_in_month: Jan=31, Feb=28/29, ...).
const daysInMonth = (iso) => {
  if (!iso) return 30;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 30;
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
};

export default function AttendanceMonthly() {
  const { user } = useAuth();
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canEdit = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const [params] = useSearchParams();
  const uploadId = params.get("upload_id");
  const [uploads, setUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(uploadId || "");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // {id, el, cl, off, reason}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/attendance/uploads").then(({ data }) => {
      const committed = data.filter((u) => u.status === "committed");
      setUploads(committed);
      if (!selectedUpload && committed.length) setSelectedUpload(committed[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const load = async (uid) => {
    if (!uid) return;
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/monthly", { params: { upload_id: uid } });
      setRows(data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (selectedUpload) load(selectedUpload); }, [selectedUpload]);

  const upload = uploads.find((u) => u.id === selectedUpload);

  const onSaveEdit = async () => {
    try {
      await api.patch(`/attendance/monthly/${editing.id}`, {
        el_credit: Number(editing.el),
        cl_credit: Number(editing.cl),
        off_count: Number(editing.off),
        reason: editing.reason || "manual adjustment",
      });
      toast.success("Updated");
      setEditing(null);
      load(selectedUpload);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const totals = useMemo(() => {
    const t = { day: 0, ot: 0, total: 0, ctc: 0, salary: 0 };
    const dim = daysInMonth(upload?.month);
    for (const r of rows) {
      t.day += r.day_count || 0;
      t.ot += r.ot_count || 0;
      t.total += r.total_count || 0;
      t.ctc += r.ctc || 0;
      // Salary approx: pro-rated by actual days_in_month
      // (matches Payroll engine divisor: Jul=31, Feb=28/29, etc.)
      t.salary += (r.ctc || 0) * (r.total_count || 0) / dim;
    }
    return t;
  }, [rows, upload?.month]);

  return (
    <div data-testid="monthly-page" className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/attendance" className="text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 2</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Monthly Summary
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {upload ? `${upload.unit_name} · ${monthLabel(upload.month)}` : "Pick a finalised upload"}
          </p>
        </div>
        <Select value={selectedUpload || undefined} onValueChange={setSelectedUpload}>
          <SelectTrigger className="w-72" data-testid="monthly-upload-select">
            <SelectValue placeholder="Select finalised upload" />
          </SelectTrigger>
          <SelectContent>
            {uploads.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.unit_name} — {monthLabel(u.month)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {user?.role === "super_admin" && selectedUpload && (
          <Button
            variant="outline"
            size="sm"
            data-testid="refresh-leaves-btn"
            className="border-amber-400 text-amber-800 hover:bg-amber-50 gap-1.5"
            disabled={loading}
            onClick={async () => {
              if (!window.confirm(
                "Re-pull EL/PL accrual + usage from Leave Ledger into this month's " +
                "summary?\n\nUse this if you approved leaves or ran Monthly Accrual " +
                "AFTER the attendance was finalized. It only refreshes leave fields — " +
                "punch counts, OT, and late deductions are untouched.\n\nProceed?",
              )) return;
              try {
                const { data } = await api.post(`/attendance/uploads/${selectedUpload}/refresh-leaves`);
                toast.success(`Refreshed leaves for ${data.updated} employees — now Recompute Payroll`);
                load(selectedUpload);
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail));
              }
            }}
            title="Re-pull EL/PL from Leave Ledger. Safe for committed months."
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh Leaves
          </Button>
        )}
      </div>

      {/* totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card label="Total day-count" value={totals.day.toFixed(1)} />
        <Card label="Total OT count" value={totals.ot.toFixed(2)} />
        <Card label="Total count (paid days)" value={totals.total.toFixed(2)} accent />
        <Card label="Indicative payroll" value={formatINR(totals.salary)} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="monthly-table">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">Punch</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Code</th>
                <th className="px-3 py-3">Department</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3 text-right">Shift</th>
                <th className="px-3 py-3 text-right">CTC</th>
                <th className="px-3 py-3 text-right">Day Cnt</th>
                <th className="px-3 py-3 text-right">OT cnt</th>
                <th className="px-3 py-3 text-right">Late</th>
                <th className="px-3 py-3 text-right text-red-700">Late Ded.</th>
                <th className="px-3 py-3 text-right">EL</th>
                <th className="px-3 py-3 text-right">CL</th>
                <th className="px-3 py-3 text-right">OFF</th>
                <th className="px-3 py-3 text-right text-emerald-700" title="Extra pay days earned by working on Sunday/holiday" data-testid="th-extra">+Extra</th>
                <th className="px-3 py-3 text-right brand-text">Total</th>
                <th className="px-3 py-3 text-right">Salary calc</th>
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={18} className="px-3 py-12 text-center text-slate-400">Loading...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={18} className="px-3 py-12 text-center text-slate-500">No data — finalize an upload first.</td></tr>
              )}
              {rows.map((r) => {
                const salary = (r.ctc || 0) * (r.total_count || 0) / daysInMonth(upload?.month);
                const isEd = editing?.id === r.id;
                return (
                  <tr key={r.id} className={`hover:bg-slate-50 ${r.is_override ? "bg-blue-50/40" : ""}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.punch_id}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{r.employee_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.employee_code}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.department_name}</td>
                    <td className="px-3 py-2 text-xs">{TYPE_LABEL[r.category]}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.shift_hours}h</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(r.ctc)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.day_count?.toFixed(1)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${(r.ot_count || 0) < 0 ? "text-red-700 font-semibold" : ""}`}>{r.ot_count?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.category === "white_collar" ? r.late_count : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700">
                      {r.late_deduction_days > 0 ? `-${r.late_deduction_days.toFixed(1)}` : "—"}
                    </td>
                    {isEd ? (
                      <>
                        <td className="px-2 py-1"><Input type="number" step="0.25" value={editing.el} onChange={(e) => setEditing({ ...editing, el: e.target.value })} className="h-7 w-16 text-xs" /></td>
                        <td className="px-2 py-1"><Input type="number" step="0.25" value={editing.cl} onChange={(e) => setEditing({ ...editing, cl: e.target.value })} className="h-7 w-16 text-xs" /></td>
                        <td className="px-2 py-1"><Input type="number" step="0.25" value={editing.off} onChange={(e) => setEditing({ ...editing, off: e.target.value })} className="h-7 w-16 text-xs" /></td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-right tabular-nums">{r.el_credit?.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.cl_credit?.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.off_count?.toFixed(2)}</td>
                      </>
                    )}
                    <td
                      className="px-3 py-2 text-right tabular-nums"
                      data-testid={`extra-days-${r.id}`}
                    >
                      {(r.extra_pay_days || 0) > 0 ? (
                        <span className="text-emerald-700 font-semibold">
                          +{Number(r.extra_pay_days).toFixed(2)}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums brand-text font-semibold">{r.total_count?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(salary)}</td>
                    <td className="px-3 py-2">
                      {canEdit && (
                        isEd ? (
                          <div className="flex gap-1">
                            <button data-testid={`save-${r.id}`} onClick={onSaveEdit} className="p-1 hover:bg-green-50 text-green-700 rounded">
                              <Save className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 text-slate-500 rounded">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            data-testid={`edit-${r.id}`}
                            onClick={() => setEditing({
                              id: r.id,
                              el: r.el_credit ?? 0,
                              cl: r.cl_credit ?? 0,
                              off: r.off_count ?? 0,
                              reason: "",
                            })}
                            className="p-1 hover:bg-slate-100 rounded text-slate-400"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${accent ? "brand-text" : "text-slate-900"}`} style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
    </div>
  );
}
